import { Button } from '@/components/ui/shadcn/button';
import { Checkbox } from '@/components/ui/shadcn/checkbox';
import { Input } from '@/components/ui/shadcn/input';
import { useTranslations } from '@/hooks/useTranslations';
import { Building2, Calendar, ClipboardList, Flag, FolderKanban, Globe, ListFilter, Loader2, Search, Tag, User, UserPlus } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';

interface FilterDefinition {
    id: string;
    label: string;
}

/**
 * One checkbox in a filter card. `group` is optional: when a filter supplies it, the
 * list is broken up by heading wherever the group changes, so pass the options already
 * ordered by group — the heading follows the order, it does not impose one.
 */
export type ToolbarFilterOption = { value: string; label: string; group?: string };

interface FilterPanelProps {
    /** Filters to render as cards. */
    filterDefinitions: FilterDefinition[];
    columnFilterOptions: Record<string, ToolbarFilterOption[]>;
    /** Per-filter loading flags. A `true` entry keeps the card visible with a spinner while its options load. */
    columnFilterLoading?: Record<string, boolean>;
    columnFilters: Record<string, string[]>;
    columnFilterSearch: Record<string, string>;
    setColumnFilterSearch: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    toggleSingleFilter: (columnId: string, value: string) => void;
    handleFilterChange: (column: string, values: string[]) => void;
    /** Optional custom controls (e.g. a date range) rendered as the first card. */
    header?: ReactNode;
    footer?: ReactNode;
}

/** When a filter has more options than this, it gets a search box and a scrollable list. */
const SEARCH_THRESHOLD = 10;

/** Best-effort icon for a filter, keyed off its id. Keeps the generic toolbar looking intentional. */
export function filterIcon(id: string): ReactNode {
    const key = id.toLowerCase();
    if (/(assign|assignee|responsible)/.test(key)) return <UserPlus className="h-4 w-4" />;
    if (/(owner|reporter|created_by|causer|colleague|users?|requester)/.test(key)) return <User className="h-4 w-4" />;
    if (/(status|state|stage)/.test(key)) return <ClipboardList className="h-4 w-4" />;
    if (/(priority|severity)/.test(key)) return <Flag className="h-4 w-4" />;
    if (/(supplier|company)/.test(key)) return <Building2 className="h-4 w-4" />;
    if (/project/.test(key)) return <FolderKanban className="h-4 w-4" />;
    if (/(country)/.test(key)) return <Globe className="h-4 w-4" />;
    if (/(date|year|created_at|delivery)/.test(key)) return <Calendar className="h-4 w-4" />;
    if (/(type|category|module|source|event|log|role|position|department)/.test(key)) return <Tag className="h-4 w-4" />;
    return <ListFilter className="h-4 w-4" />;
}

/**
 * Body of the filter modal: a responsive grid of filter cards. Each card carries an icon,
 * a title and its checkbox list (searchable + scrollable once it exceeds SEARCH_THRESHOLD).
 * The surrounding Dialog (see `FilterModal`) owns the header, chips, footer and Clear/Apply.
 */
export function FilterPanel({
    filterDefinitions,
    columnFilterOptions,
    columnFilterLoading,
    columnFilters,
    columnFilterSearch,
    setColumnFilterSearch,
    toggleSingleFilter,
    handleFilterChange,
    header,
    footer,
}: FilterPanelProps) {
    const { t } = useTranslations();

    // Keep a filter card visible while its options are still loading, otherwise it would only pop
    // into existence once the async fetch resolves.
    const visibleFilters = filterDefinitions.filter(
        (filter) => (columnFilterOptions[filter.id] || []).length > 0 || columnFilterLoading?.[filter.id],
    );

    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {header && <div className="rounded-xl border border-border bg-card p-4">{header}</div>}


            {visibleFilters.map((filter) => {
                const options = columnFilterOptions[filter.id] || [];
                const selected = columnFilters[filter.id] || [];
                const isLoading = !!columnFilterLoading?.[filter.id];
                const showSearch = options.length > SEARCH_THRESHOLD;
                const searchValue = columnFilterSearch[filter.id] || '';
                const displayedOptions =
                    showSearch && searchValue
                        ? options.filter((option) => option.label.toLowerCase().includes(searchValue.toLowerCase()))
                        : options;

                return (
                    <div key={filter.id} className="flex flex-col rounded-xl border border-border bg-card p-4">
                        <div className="mb-3 flex items-start gap-3">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                {filterIcon(filter.id)}
                            </span>
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-foreground">{filter.label}</p>
                            </div>
                            {selected.length > 0 && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleFilterChange(filter.id, [])}
                                    className="h-auto shrink-0 p-0 text-xs font-normal text-muted-foreground hover:bg-transparent hover:text-destructive"
                                >
                                    {t('common.clear', 'Clear')}
                                </Button>
                            )}
                        </div>

                        {showSearch && (
                            <div className="relative mb-2">
                                <Search className="absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    className="h-9 pl-8"
                                    placeholder={t('common.search_placeholder', 'Search...')}
                                    value={searchValue}
                                    onChange={(event) =>
                                        setColumnFilterSearch((prev) => ({
                                            ...prev,
                                            [filter.id]: event.target.value,
                                        }))
                                    }
                                />
                            </div>
                        )}

                        <div className={showSearch ? 'max-h-52 space-y-0.5 overflow-y-auto pr-1' : 'space-y-0.5'}>
                            {displayedOptions.length === 0 ? (
                                isLoading ? (
                                    <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        {t('common.loading', 'Loading...')}
                                    </div>
                                ) : (
                                    <p className="px-2 py-1.5 text-sm text-muted-foreground">{t('common.no_results', 'No results')}</p>
                                )
                            ) : (
                                displayedOptions.map((option, index) => {
                                    const checked = selected.includes(option.value);
                                    const id = `${filter.id}-${option.value}`;
                                    // A heading wherever the group changes. Searching narrows the
                                    // list without reordering it, so the headings stay correct —
                                    // an emptied group simply drops its heading with it.
                                    const showGroupHeading = !!option.group && option.group !== displayedOptions[index - 1]?.group;
                                    return (
                                        <Fragment key={option.value}>
                                            {showGroupHeading && (
                                                <p className="text-muted-foreground px-2 pt-2 pb-1 text-xs font-semibold tracking-wide first:pt-0">
                                                    {option.group}
                                                </p>
                                            )}
                                            <label
                                                htmlFor={id}
                                                className="hover:bg-accent flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors"
                                            >
                                                <Checkbox
                                                    id={id}
                                                    checked={checked}
                                                    onCheckedChange={() => toggleSingleFilter(filter.id, option.value)}
                                                />
                                                <span className="truncate text-sm" title={option.label}>
                                                    {option.label}
                                                </span>
                                            </label>
                                        </Fragment>
                                    );
                                })
                            )}
                        </div>
                    </div>
                );
            })}
            {footer && <div className="rounded-xl border border-border bg-card p-4">{footer}</div>}
        </div>
    );
}
